import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import * as moment from 'moment';
import { TramApiService } from '../tram-api.service';
import { ArticleItem, ArticleType } from '../core/article';
import { UsingMode } from '../core/review';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { NewsService } from '../providers/news.service';
import { ToasterService } from 'angular5-toaster/angular5-toaster';
import { StorageService } from '../storage.service';

import { environment } from '../../environments/environment';

@Component({
    selector: 'article',
    templateUrl: './article.component.html',
    styleUrls: ['./article.component.scss']
})
export class ArticleComponent implements OnInit {
    private readonly rootUrl = environment.apiUrl;
    private readonly convertedImageType = 'image/jpeg';
    private readonly convertedImageWidth = 1000;
    private readonly convertedImageQuality = 0.92;
    private activatedRouteParams: ParamMap;
    private previewImageSource: string;
    UsingMode = UsingMode;
    ArticleType = ArticleType;

    id: string;
    title: string;
    publishDate: moment.Moment;
    contentItems: ArticleItem[];

    mode: UsingMode = UsingMode.Anonymous;
    isNewArticle: boolean;

    articleForm: FormGroup;

    isDragActive = false;
    preview: ImageFormControl;

    readonly defaultSrc = './assets/news-data/gap.png';

    isLoading = false;
    get isMobile() {
        return (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
    }

    constructor(
        private router: Router,
        private api: TramApiService,
        private activatedRoute: ActivatedRoute,
        private newsService: NewsService,
        private toaster: ToasterService,
        private storage: StorageService) {
        activatedRoute.paramMap.subscribe(p => {
            if (p.keys.length <= 0) {
                return;
            }

            this.activatedRouteParams = p;
        });
    }

    ngOnInit() {
        this.mode = UsingMode.Anonymous;
        this.isNewArticle = false;
        this.contentItems = [];

        const id = this.getId();
        if (id === -1) {
            this.preview = new ImageFormControl(null, Validators.required);

            const firstItem = new ExtendedFormControl(null, Validators.required);
            firstItem.type = ArticleType.Text;

            this.articleForm = new FormGroup({
                'title': new FormControl(null, Validators.required),
                'preview': this.preview,
                'items': new FormArray([firstItem])
            });
        } else {
            this.isLoading = true;

            if (this.storage.isSensitiveDataExist.value) {
                this.api
                    .getCurrentUser()
                    .then(user => {
                        this.mode = user && user.isModerator
                            ? UsingMode.God
                            : UsingMode.Anonymous;

                        return this.mode;
                    }).then(mode => {
                        const result = this.newsService.getArticle(id);
                        result.subscribe(a => {
                            this.id = a.id;
                            this.title = a.title;
                            this.previewImageSource = a.previewSrc;
                            this.contentItems = a.items;
                            this.publishDate = a.publishDate;

                            if (this.mode === UsingMode.God) {
                                this.initFormByArticle();
                            }

                            this.isLoading = false;
                        }, error => {
                            this.toaster.pop('error', null, error);
                            this.isLoading = false;
                        });
                    });
            } else {
                const result = this.newsService.getArticle(id);
                result.subscribe(a => {
                    this.id = a.id;
                    this.title = a.title;
                    this.previewImageSource = a.previewSrc;
                    this.contentItems = a.items;
                    this.publishDate = a.publishDate;

                    this.isLoading = false;
                }, error => {
                    this.toaster.pop('error', null, error);
                    this.isLoading = false;
                });
            }
        }
    }

    navigateBack() {
        if (this.articleForm) {
            this.articleForm.reset();
        }

        this.router.navigate(['/news']);
    }

    addTextControl() {
        const newItem = new ExtendedFormControl(null, Validators.required);
        newItem.type = ArticleType.Text;
        (<FormArray>this.articleForm.controls['items']).push(newItem);
    }
    addImageControl() {
        const newItem = new ImageFormControl(null, Validators.required);
        newItem.type = ArticleType.Image;
        (<FormArray>this.articleForm.controls['items']).push(newItem);
    }
    addCarouselControl() {
        const newItem = new ExtendedFormControl(null);
        newItem.type = ArticleType.Carousel;
        (<FormArray>this.articleForm.controls['items']).push(newItem);
    }
    removeControl(index: number) {
        (<FormArray>this.articleForm.controls['items']).removeAt(index);
    }

    onDragOver(event, item: ImageFormControl) {
        event.stopPropagation();
        event.preventDefault();
        item.isDragActive = true;
    }
    onDragLeave(event, item: ImageFormControl) {
        event.stopPropagation();
        event.preventDefault();
        item.isDragActive = false;
    }
    onDrop(event, item: ImageFormControl, index: number) {
        event.stopPropagation();
        event.preventDefault();

        try {
            item.isDragActive = false;
            const files = event.target.files || event.dataTransfer.files;
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                const r = new FileReader();
                r.onload = (e) => {
                    const target: any = e.target; // this is workaround for weird error in console
                    const data = target.result;

                    this.scaleImage(data, this.convertedImageType, this.convertedImageWidth, this.convertedImageQuality)
                        .then(convertedData => {
                            item.data = convertedData;
                            item.filename = file.name;

                            if (!item.value) {
                                item.setValue(file.name);
                            }
                        });
                };
                r.onerror = (e) => { };
                r.readAsDataURL(file);
            }
        } catch (er) {
            item.isDragActive = false;
        }
    }
    onDropPreview(event, item: ImageFormControl) {
        event.stopPropagation();
        event.preventDefault();

        try {
            item.isDragActive = false;
            const files = event.target.files || event.dataTransfer.files;
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                const r = new FileReader();
                r.onload = (e) => {
                    const target: any = e.target; // this is workaround for weird error in console
                    const data = target.result;

                    this.scaleImage(data, this.convertedImageType, this.convertedImageWidth, this.convertedImageQuality)
                        .then(convertedData => {
                            item.data = convertedData;
                            item.filename = file.name;

                            if (!item.value) {
                                this.preview.setValue(file.name);
                            }
                        });
                };
                r.onerror = (e) => { };
                r.readAsDataURL(file);
            }
        } catch (er) {
            item.isDragActive = false;
        }
    }

    onSubmit() {
        const model = this.articleForm.value;

        const tempArticle = new NewArticle();
        tempArticle.articleDate = moment();
        tempArticle.title = model.title;
        tempArticle.image = this.preview.data;
        const items = (<FormArray>this.articleForm.controls['items']).controls;
        tempArticle.articleItems = items.map(i => {
            const item = new NewArticleItem();
            if (i instanceof ImageFormControl) {
                item.itemType = i.type;
                item.itemContent = i.data;
            } else if (i instanceof ExtendedFormControl) {
                item.itemType = i.type;
                item.itemContent = i.value;
            }

            return item;
        });

        const serializedModel = JSON.stringify(tempArticle);

        if (this.isNewArticle) {
            const result = this.newsService.publish(serializedModel);
            result.subscribe(newArticle => {
                this.toaster.pop('success', null, 'Статья опубликована');
                this.navigateBack();
            }, error => {
                this.toaster.pop('error', null, error);
            });
        } else {
            const result = this.newsService.update(this.id, serializedModel);
            result.subscribe(article => {
                this.toaster.pop('success', null, 'Статья обновлена');
                this.navigateBack();
            }, error => {
                this.toaster.pop('error', null, error);
            });
        }
    }

    resetPreview() {
        this.preview.data = null;
        this.preview.filename = null;

        const previewInput = document.getElementById('preview') as HTMLInputElement;
        previewInput.value = '';

        this.preview.reset();
    }

    private getId() {
        let id: string;
        if (this.activatedRouteParams) {
            if (this.activatedRouteParams.has('id')) {
                id = this.activatedRouteParams.get('id');

                if (id === 'new') {
                    this.isNewArticle = true;
                    return -1;
                } else {
                    return id;
                }
            }
        }
    }
    private initFormByArticle() {
        this.preview = new ImageFormControl(this.previewImageSource, Validators.required);
        this.preview.data = this.previewImageSource;

        const itemControls = new FormArray([]);
        this.contentItems.forEach(i => {
            let itemControl: ExtendedFormControl;
            if (i.type === ArticleType.Text) {
                itemControl = new ExtendedFormControl(i.content, Validators.required);
            } else if (i.type === ArticleType.Image) {
                itemControl = new ImageFormControl(i.content, Validators.required);
                (<ImageFormControl>itemControl).data = i.content;
            }
            itemControl.type = i.type;
            itemControls.push(itemControl);
        });

        this.articleForm = new FormGroup({
            'title': new FormControl(this.title, Validators.required),
            'preview': this.preview,
            'items': itemControls
        });
    }

    private async scaleImage(dataUrl: string, imageType: string, resolution: number, quality: number): Promise<string> {
        // Create a temporary image so that we can compute the height of the image.
        const image = await this.getImage(dataUrl);
        const oldWidth = image.naturalWidth;
        const oldHeight = image.naturalHeight;

        const longestDimension = oldWidth > oldHeight ? 'width' : 'height';
        const currentRes = longestDimension === 'width' ? oldWidth : oldHeight;

        if (currentRes > resolution) {
            // Calculate new dimensions
            const newSize = longestDimension === 'width'
                ? Math.floor(oldHeight / oldWidth * resolution)
                : Math.floor(oldWidth / oldHeight * resolution);
            const newWidth = longestDimension === 'width' ? resolution : newSize;
            const newHeight = longestDimension === 'height' ? resolution : newSize;

            // Create a temporary canvas to draw the downscaled image on.
            const canvas = document.createElement('canvas');
            canvas.width = newWidth;
            canvas.height = newHeight;

            // Draw the downscaled image on the canvas and return the new data URL.
            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0, newWidth, newHeight);
            const newDataUrl = canvas.toDataURL(imageType, quality);
            return newDataUrl;
        } else {
            return dataUrl;
        }
    }

    private getImage(dataUrl: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = dataUrl;
            image.onload = () => {
                resolve(image);
            };
            image.onerror = ((el: any, err: ErrorEvent) => {
                reject(err.error);
            }) as any;
        });
    }
}

class ExtendedFormControl extends FormControl {
    type: ArticleType = ArticleType.Text;
}

class ImageFormControl extends ExtendedFormControl {
    type: ArticleType = ArticleType.Image;
    data: string;
    filename: string;
    isDragActive: boolean;
}

class NewArticle {
    articleDate: moment.Moment;
    title: string;
    image: string;
    articleItems: NewArticleItem[];
}

class NewArticleItem {
    itemType: string;
    itemContent: string;
}



// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/pages/article.component.ts